import { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ButtonPrimary from '../shared/Button/PrimaryButton';
import ButtonSecondary from '../shared/Button/SecondaryButton';
import { SiteRecruitingStudy } from '../SiteMaster/types';
import {
  SITE_MASTER_COLORS,
  StSiteMasterChip,
  StSiteMasterChipList,
  StSiteMasterStudyCard,
  StSiteMasterStudyCardActions,
  StSiteMasterStudyCardHeader,
  StSiteMasterStudyCardStatus,
} from '../SiteMaster/SiteMaster.styles';

type SiteRecruitingStudyCardProps = {
  study: SiteRecruitingStudy;
  viewStudyDetailsLabel: string | React.ReactElement;
  eligibilityLabel: string | React.ReactElement;
  enrollingStatusLabel: string;
  titleLineCount?: number;
  descriptionLineCount?: number;
};

const DEFAULT_VISIBLE_CONDITIONS = 2;

const conditionChipSx = {
  backgroundColor: SITE_MASTER_COLORS.accentSurface,
  color: SITE_MASTER_COLORS.teal,
  whiteSpace: 'nowrap' as const,
  maxWidth: '100%',
};

const phaseChipSx = {
  backgroundColor: SITE_MASTER_COLORS.chipNeutralBg,
  color: SITE_MASTER_COLORS.textPrimary,
  whiteSpace: 'nowrap' as const,
};

const moreLinkSx = {
  border: 'none',
  background: 'none',
  padding: 0,
  margin: 0,
  cursor: 'pointer',
  color: SITE_MASTER_COLORS.teal,
  fontSize: '14px',
  fontWeight: 500,
  lineHeight: 1.4,
  fontFamily: 'inherit',
  alignSelf: 'center',
};

const lineClampSx = (lines: number) => ({
  display: '-webkit-box',
  WebkitLineClamp: lines,
  WebkitBoxOrient: 'vertical' as const,
  overflow: 'hidden',
});

const SiteRecruitingStudyCard = ({
  study,
  viewStudyDetailsLabel,
  eligibilityLabel,
  enrollingStatusLabel,
  titleLineCount = 1,
  descriptionLineCount = 3,
}: SiteRecruitingStudyCardProps): JSX.Element => {
  const [conditionsExpanded, setConditionsExpanded] = useState(false);

  const hasEligibility = Boolean(study.eligibilityUrl);
  const hasPhase = Boolean(study.phase);
  const conditions = study.conditionLabels;
  const hiddenCount = Math.max(0, conditions.length - DEFAULT_VISIBLE_CONDITIONS);
  const visibleConditions = conditionsExpanded
    ? conditions
    : conditions.slice(0, DEFAULT_VISIBLE_CONDITIONS);
  const showToggle = hiddenCount > 0;
  const showChipList = visibleConditions.length > 0 || showToggle || hasPhase;

  const toggleConditions = () => setConditionsExpanded((prev) => !prev);

  return (
    <StSiteMasterStudyCard>
      <StSiteMasterStudyCardHeader reserveStatusSpace={showChipList}>
        {showChipList ? (
          <StSiteMasterChipList>
            {visibleConditions.map((label, index) => (
              <StSiteMasterChip key={`${label}-${index}`} sx={conditionChipSx}>
                {label}
              </StSiteMasterChip>
            ))}

            {showToggle ? (
              <Box
                component="li"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  listStyle: 'none',
                  '& > button': moreLinkSx,
                }}
              >
                <button
                  type="button"
                  aria-expanded={conditionsExpanded}
                  aria-label={conditionsExpanded ? 'Show less' : `+${hiddenCount} more`}
                  onClick={toggleConditions}
                >
                  {conditionsExpanded ? 'Show less' : `+${hiddenCount} more`}
                </button>
              </Box>
            ) : null}

            {hasPhase ? <StSiteMasterChip sx={phaseChipSx}>{study.phase}</StSiteMasterChip> : null}
          </StSiteMasterChipList>
        ) : null}

        <StSiteMasterStudyCardStatus>
          <span className="status-dot" aria-hidden />
          <span className="status-label">{enrollingStatusLabel}</span>
        </StSiteMasterStudyCardStatus>
      </StSiteMasterStudyCardHeader>

      {(study.title || study.description) && (
        <div>
          {study.title ? (
            <Typography
              component="h3"
              sx={{
                margin: 0,
                fontSize: '16px',
                fontWeight: 700,
                lineHeight: '22px',
                color: SITE_MASTER_COLORS.textPrimary,
                ...lineClampSx(titleLineCount),
              }}
            >
              {study.title}
            </Typography>
          ) : null}

          {study.description ? (
            <Typography
              sx={{
                margin: study.title ? '8px 0 0' : 0,
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: 1.5,
                color: SITE_MASTER_COLORS.textSecondary,
                ...lineClampSx(descriptionLineCount),
              }}
            >
              {study.description}
            </Typography>
          ) : null}
        </div>
      )}

      <StSiteMasterStudyCardActions>
        <ButtonSecondary href={study.detailsUrl}>{viewStudyDetailsLabel}</ButtonSecondary>

        {hasEligibility ? (
          <ButtonPrimary href={study.eligibilityUrl}>{eligibilityLabel}</ButtonPrimary>
        ) : null}
      </StSiteMasterStudyCardActions>
    </StSiteMasterStudyCard>
  );
};

export default SiteRecruitingStudyCard;
